import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectError,
  selectStats,
  selectVersionAnalytics,
  selectDeviceAnalytics,
  deleteStaleEntries,
} from "src/store/presenceSlice";
import type { AppDispatch } from "../store";
import DashboardError from "../Error";
import { FcAndroidOs } from "react-icons/fc";
import { FaApple } from "react-icons/fa";
import { Trash2, Loader2, ExternalLink } from "lucide-react";
import PlatformCard from "./PlatformCard";
import NumberTicker from "./NumberTicker";
import AnalyticsSection from "./AnalyticsSection";
import { selectUserProfile } from "../store/authSlice";
import { hasPermission, Permission, UserRole } from "../lib/roles";

type CleanupState = "idle" | "loading" | "done";

const Dashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const error = useSelector(selectError);
  const stats = useSelector(selectStats);
  const versionBreakdowns = useSelector(selectVersionAnalytics);
  const deviceBreakdowns = useSelector(selectDeviceAnalytics);

  const userProfile = useSelector(selectUserProfile);
  const hasAnalyticsPermission = hasPermission(
    userProfile?.role ?? UserRole.VIEWER,
    Permission.VIEW_ANALYTICS,
  );
  const canCleanup = hasPermission(
    userProfile?.role ?? UserRole.VIEWER,
    Permission.CHANGE_SETTINGS,
  );

  if (error) {
    return <DashboardError />;
  }
  return (
    <div className="relative flex min-h-[calc(100dvh-5.5rem)] flex-col items-center justify-center px-4 pb-8 pt-14 sm:px-6 md:min-h-[calc(100dvh-1px)] md:py-6 md:pt-6">
      <div className="text-center mb-8 sm:mb-12">
        <div className="h-4 sm:h-8 lg:h-12" aria-hidden />
        <h2 className="text-xl text-gray-300 mb-3 sm:text-2xl">
          Total Active Users
        </h2>
        <NumberTicker
          value={stats.total}
          className="text-6xl sm:text-8xl md:text-[10rem] font-extrabold tracking-tight drop-shadow-lg"
        />
      </div>

      <div className="grid w-full max-w-3xl grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8">
        <PlatformCard
          icon={<FcAndroidOs />}
          label="Android Users"
          value={stats.android}
          valueColor="text-green-400"
          bgColor="bg-green-500/10 backdrop-blur-xl"
        />
        <PlatformCard
          icon={<FaApple className="text-indigo-400" />}
          label="iOS Users"
          value={stats.ios}
          valueColor="text-indigo-400"
          bgColor="bg-indigo-500/10 backdrop-blur-xl"
        />
      </div>

      <div className="mt-8 text-sm text-gray-400 sm:mt-10">
        Last update:{" "}
        {stats.lastUpdateMs
          ? new Date(stats.lastUpdateMs).toLocaleString()
          : "-"}
      </div>
      {canCleanup && <StaleCleanupButton dispatch={dispatch} />}
      {hasAnalyticsPermission && (
        <AnalyticsSection
          versionBreakdowns={versionBreakdowns}
          deviceBreakdowns={deviceBreakdowns}
        />
      )}
    </div>
  );
};

const StaleCleanupButton: React.FC<{ dispatch: AppDispatch }> = ({
  dispatch,
}) => {
  const [cleanupState, setCleanupState] = useState<CleanupState>("idle");
  const [deletedCount, setDeletedCount] = useState(0);

  const handleCleanup = useCallback(async () => {
    setCleanupState("loading");
    try {
      const count = await dispatch(deleteStaleEntries());
      setDeletedCount(count);
      setCleanupState("done");
      setTimeout(() => setCleanupState("idle"), 4000);
    } catch {
      setCleanupState("idle");
    }
  }, [dispatch]);

  return (
    <div className="flex flex-col items-center gap-1.5 mt-4">
      <button
        onClick={handleCleanup}
        disabled={cleanupState === "loading"}
        title="Delete stale presence entries from Firebase"
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border border-white/10 bg-white/5 hover:bg-red-500/20 hover:border-red-500/40 hover:text-red-300 text-gray-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {cleanupState === "loading" ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Trash2 className="w-4 h-4" />
        )}
        {cleanupState === "done"
          ? `Removed ${deletedCount} stale`
          : cleanupState === "loading"
            ? "Cleaning…"
            : "Clear Stale"}
      </button>
      <a
        href="https://gitlab.com/ivueit/iv-live-presence/-/work_items/1"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-400 transition-colors"
      >
        <ExternalLink className="w-3 h-3" />
        Temporary — see issue #1
      </a>
    </div>
  );
};

export default Dashboard;
